<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcFixedAssetWorkItemsPanel',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const filterItems = (...args) => {
    const [items, displayAll, displayCount] = args;
    if (displayAll || displayCount == items.length) {
      return items;
    }
    return _.take(items, displayCount);
  };

  const items = ref(null);
  const displayAll = ref(false);
  const displayCount = ref(5);
  const ux = ref([]);

  const $data = computed(() => ({
    items: items.value,
    displayAll: displayAll.value,
    displayCount: displayCount.value,
    ux: ux.value,
  }));

  const $$load_items = () => {
    return $fn.value
      .fetch('list_fixed_asset_works', {
        parameters: { fixed_asset_id: props.oid },
      })
      .then((p) =>
        _.chain(p)
          .orderBy(p, ['date', 'id'], ['asc', 'asc'])
          .groupBy('worksheet_id')
          .map((items, worksheet_id) => ({ ...items[0], items }))
          .value()
      );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      {
        name: 'displayAll',
        cache: { type: 'local', key: 'FixedAsset.WorkItems.displayAll' },
      },
      { name: 'displayCount' },
      { name: 'ux' },
    ],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: { items, displayAll, displayCount, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const canDisplayMoreItems = computed(() => {
    if (loaded.value) {
      return (
        !displayAll.value &&
        items.value &&
        displayCount.value < items.value.length
      );
    }
    return null;
  });
  const canHideItems = computed(() => {
    if (loaded.value) {
      return (
        displayAll.value &&
        items.value &&
        displayCount.value < items.value.length
      );
    }
    return null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );

  const act_7736732573189513_setData_bdl0thpen_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'displayAll',
      value: false,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2912679705172878_setData_5vms12cd5_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'displayAll',
      value: true,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };

  const act_7315092382398562_reloadComponentData_vmx42iq4o =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_w8jab15br =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_vmx42iq4o,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_w8jab15br],
    event: 'reload',
  });
  const act_7736732573189513_setData_bdl0thpen = new actionTypes.SetDataAction({
    actionArgs: act_7736732573189513_setData_bdl0thpen_getActionArgs,
  });
  const inst_evh_7736732573189513_clickPreventStop_5m5fpsoi7 =
    new actionTypes.EventHandlerImpl({
      action: act_7736732573189513_setData_bdl0thpen,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const inst_evh_7736732573189513_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7736732573189513_clickPreventStop_5m5fpsoi7],
      event: 'click.prevent.stop',
    });
  const act_2912679705172878_setData_5vms12cd5 = new actionTypes.SetDataAction({
    actionArgs: act_2912679705172878_setData_5vms12cd5_getActionArgs,
  });
  const inst_evh_2912679705172878_clickPreventStop_4egnb6efh =
    new actionTypes.EventHandlerImpl({
      action: act_2912679705172878_setData_5vms12cd5,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const inst_evh_2912679705172878_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2912679705172878_clickPreventStop_4egnb6efh],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_w8jab15br,
    inst_evh_7315092382398562_reload,
    inst_evh_7736732573189513_clickPreventStop_5m5fpsoi7,
    inst_evh_7736732573189513_clickPreventStop,
    inst_evh_2912679705172878_clickPreventStop_4egnb6efh,
    inst_evh_2912679705172878_clickPreventStop,
  };

  const evh_7315092382398562_reload_w8jab15br = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_w8jab15br.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7736732573189513_clickPreventStop_5m5fpsoi7 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7736732573189513_clickPreventStop_5m5fpsoi7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7736732573189513_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7736732573189513_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2912679705172878_clickPreventStop_4egnb6efh = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2912679705172878_clickPreventStop_4egnb6efh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2912679705172878_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2912679705172878_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('FixedAssetWorkItems', 'Works') }}
          </strong>
          <strong class="mx-2" v-if="items && items.length">
            ({{ items.length }})
          </strong>
        </ItpBox>
      </template>
      <BListGroup v-if="loaded">
        <BListGroupItem v-if="!items.length">
          <span>
            {{ pgettext('FixedAssetWorkItems', 'No related items.') }}
          </span>
        </BListGroupItem>
        <BListGroupItem
          :key="item.id"
          class="d-flex flex-column"
          v-for="item in filterItems(items, displayAll, displayCount)"
        >
          <table class="table table-borderless table-sm m-0">
            <tbody>
              <tr>
                <td>
                  <ItpLink
                    :text="item.worksheet_number"
                    :to="{
                      name: 'app.worksheet',
                      params: { id: item.worksheet_id },
                    }"
                  >
                  </ItpLink>
                </td>
                <td>
                  <ItpText muted strong spacing="me-4">
                    {{ date(item.worksheet_date) }}
                  </ItpText>
                </td>
                <td>
                  <ItpLink
                    :text="item.rental_name"
                    :to="{
                      name: 'app.rental',
                      params: { id: item.rental_id },
                    }"
                  >
                  </ItpLink>
                </td>
                <td>
                  <ItpLink
                    :text="item.client_name"
                    :to="{
                      name: 'app.company',
                      params: { id: item.client_id },
                    }"
                  >
                  </ItpLink>
                </td>
              </tr>
              <tr v-if="item.worksheet_description">
                <td :colspan="4">
                  <ItpText :text="item.worksheet_description"> </ItpText>
                </td>
              </tr>
              <tr>
                <td :colspan="4">
                  <table class="table table-borderless table-sm m-0">
                    <tbody>
                      <tr v-for="work in item.items">
                        <td>
                          <ItpText muted strong spacing="me-4">
                            {{ date(work.work_item_date) }}
                          </ItpText>
                        </td>
                        <td>
                          <ItpText strong spacing="me-3">
                            {{ number(work.quantity) }} {{ work.unit }}
                          </ItpText>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </BListGroupItem>
        <BListGroupItem v-if="canHideItems || canDisplayMoreItems">
          <ItpBox justifyContent="center">
            <ItpLink
              :text="gettext('Show less')"
              v-if="canHideItems"
              @click.prevent.stop="
                evh_7736732573189513_clickPreventStop($event, {})
              "
            >
            </ItpLink>
            <ItpLink
              :text="gettext('Show more')"
              v-if="canDisplayMoreItems"
              @click.prevent.stop="
                evh_2912679705172878_clickPreventStop($event, {})
              "
            >
            </ItpLink>
          </ItpBox>
        </BListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
